import { useRef, useState, useEffect, useMemo } from "react";
import { toPng } from "html-to-image";
import ScheduleImageTemplate, {
  SAMPLE_ENTRIES,
  buildStressEntries,
} from "./ScheduleImageTemplate";
import { buildScheduleEntries } from "./scheduleImageData";

// studioKey ↔ studio_id（1浜線/2白山/3帯山/4健軍）
const STUDIO_ID = { hamasen: 1, hakuzan: 2, obiyama: 3, kengun: 4 };

// ───────── 備考（Schedule.note）表示の検証ケース ─────────
// 実運用の密度で「どのくらいの備考なら破綻しないか」を目視するためのデータ。
const R_SHORT = "発表会準備のため";                       // 8文字
const R_MID = "3/7日分の時間を割らせていただきます";        // 19文字
const R_LONG = "講師都合により休講します。振替日は決まり次第、公式LINEにてご案内いたします"; // 37文字

const cls = (t) => `【${t}】`;
const L = {
  a: cls("18:00 - 19:00 MIRI HIPHOP 初級"),
  b: cls("19:00 - 20:00 CHANRIO K-POP 初級"),
  c: cls("20:00 - 21:00 SHUN POP 初級"),
  d: cls("21:00 - 22:00 SHUN LOCK 初級"),
  e: cls("19:30 - 20:30 CHANRIO JAZZFUNK 中級"),
};
const closed = (text, remark) => ({ text, note: "⚠休講", noteType: "red", remark });
// 振替元（休講＋振替先併記）＝横幅がいちばん厳しいケース
const closedTo = (text, remark) => ({
  text,
  note: "⚠休講 →7/4(土) 19:00-20:00へ振替",
  noteType: "red",
  remark,
});
const timeCh = (text, remark) => ({ text, note: "→ 19:30-20:30", noteType: "green", remark });
const openL = (text) => ({ text: `${text}通常営業` });

// ケース1: 通常密度 × 短い備考
const CASE_LIGHT = {
  3: { badge: "partialClosed", lines: [closed(L.a, R_SHORT), openL(L.b)] },
  10: { badge: "fullClosed", lines: [] },
  15: { badge: "fullTimeChange", lines: [timeCh(L.e, R_SHORT)] },
  22: { badge: "partialClosed", lines: [closed(L.b, R_SHORT), openL(L.c), openL(L.d)] },
  28: { badge: "fullSubstitute", lines: [{ text: L.e, note: "※AKARI代講", noteType: "green" }] },
};

// ケース2: 振替先併記あり × 中くらいの備考（19文字）
const CASE_MID = {
  3: { badge: "partialClosed", lines: [closedTo(L.a, R_MID), openL(L.b)] },
  9: { badge: "partialTimeChange", lines: [timeCh(L.e, R_MID), openL(L.c)] },
  15: { badge: "fullClosed", lines: [] },
  17: { badge: "partialClosed", lines: [closedTo(L.b, R_MID), closed(L.c, R_SHORT), openL(L.d)] },
  24: { badge: "partialClosed", lines: [closed(L.a, R_MID), openL(L.b), openL(L.c)] },
};

// ケース3: 長文備考（37文字）。折り返しの効きを見る
const CASE_LONG = {
  3: { badge: "partialClosed", lines: [closed(L.a, R_LONG), openL(L.b)] },
  9: { badge: "partialClosed", lines: [closedTo(L.e, R_LONG), openL(L.c)] },
  15: { badge: "fullTimeChange", lines: [timeCh(L.e, R_LONG)] },
  21: { badge: "partialClosed", lines: [closed(L.b, R_LONG), openL(L.c), openL(L.d)] },
  27: { badge: "partialClosed", lines: [closed(L.a, R_MID), closed(L.b, R_LONG), openL(L.c)] },
};

// ケース4: 極端（全日 4 クラス × 全行に 19 文字備考）＝縦の限界
function buildRemarkStress(daysInMonth) {
  const e = {};
  for (let d = 1; d <= daysInMonth; d++) {
    e[d] = {
      badge: "partialClosed",
      lines: [closed(L.a, R_MID), closed(L.b, R_MID), closed(L.c, R_MID), closed(L.d, R_MID)],
    };
  }
  return e;
}

// ケース5: 超長文（62文字）＝折り返しが発生する長さ
const R_XLONG =
  "講師都合により休講いたします。振替日が決まり次第、公式LINEおよび教室掲示にてご案内いたしますので、恐れ入りますがご確認をお願いします";
const CASE_XLONG = {
  3: { badge: "partialClosed", lines: [closed(L.a, R_XLONG), openL(L.b)] },
  9: { badge: "partialClosed", lines: [closedTo(L.e, R_XLONG), openL(L.c)] },
  15: { badge: "fullTimeChange", lines: [timeCh(L.e, R_XLONG)] },
  21: { badge: "partialClosed", lines: [closed(L.b, R_XLONG), openL(L.c), openL(L.d)] },
  27: { badge: "partialClosed", lines: [closed(L.a, R_XLONG), closed(L.b, R_LONG), openL(L.c)] },
};

const REMARK_CASES = {
  "remark-light": CASE_LIGHT,
  "remark-mid": CASE_MID,
  "remark-long": CASE_LONG,
  "remark-xlong": CASE_XLONG,
};

// API ベース：ローカル dev（admin Django 未起動）では本番 admin API にフォールバック。
// 本番 API は CORS 開放 + 認証なしのため、ローカルブラウザからも実データ取得できる。
const ENV_API = process.env.REACT_APP_API_URL || "";
const API_BASE =
  !ENV_API || ENV_API.includes("localhost")
    ? "https://admin.dancestudio-ivy.com"
    : ENV_API;

// Phase 7-G テンプレートのプレビュー兼挙動確認ページ。
// URL 直打ち専用 (/html-to-image-demo)。サイドバー非掲載。
export default function HtmlToImageDemo() {
  const targetRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lastInfo, setLastInfo] = useState(null);
  const [studioKey, setStudioKey] = useState("hamasen");
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(4);
  const [scale, setScale] = useState(0.45);
  // データソース: "sample"（サンプル）/ "real"（実 API）/ "stress"（負荷）
  const [source, setSource] = useState("real");
  const [realData, setRealData] = useState(null); // { placements, overrides }
  const [loadError, setLoadError] = useState("");
  const [fitInfo, setFitInfo] = useState({ fitScale: 1, overflow: false });

  const studioId = STUDIO_ID[studioKey];
  const daysInMonth = new Date(year, month, 0).getDate();

  // 実データ取得（source=real かつ 校舎/年月変更時）
  useEffect(() => {
    if (source !== "real") return;
    let cancelled = false;
    setLoadError("");
    setRealData(null);
    const j = (url) =>
      fetch(url).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      });
    Promise.all([
      j(`${API_BASE}/api/get_lesson_placements/?studio_id=${studioId}`),
      j(`${API_BASE}/api/get_schedule_overrides/?year=${year}&month=${month}`),
    ])
      .then(([placementsRes, overridesRes]) => {
        if (cancelled) return;
        setRealData({
          placements: placementsRes.placements || [],
          overrides: overridesRes || {},
        });
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message || "取得失敗");
      });
    return () => {
      cancelled = true;
    };
  }, [source, studioId, year, month]);

  const entries = useMemo(() => {
    if (source === "stress") return buildStressEntries(daysInMonth);
    if (source === "sample") return SAMPLE_ENTRIES;
    if (source === "remark-stress") return buildRemarkStress(daysInMonth);
    if (REMARK_CASES[source]) return REMARK_CASES[source];
    // real
    if (!realData) return {};
    return buildScheduleEntries({
      year,
      month,
      studioId,
      placements: realData.placements,
      overridesResponse: realData.overrides,
    });
  }, [source, realData, year, month, studioId, daysInMonth]);

  const handleDownload = async () => {
    if (!targetRef.current || busy) return;
    setBusy(true);
    setLastInfo(null);
    try {
      const startedAt = performance.now();
      const dataUrl = await toPng(targetRef.current, {
        pixelRatio: 1,
        width: 1080,
        height: 1920,
        cacheBust: true,
        backgroundColor: "#ffffff",
      });
      const elapsedMs = Math.round(performance.now() - startedAt);
      const base64 = dataUrl.split(",")[1] || "";
      const sizeKb = Math.round((base64.length * 0.75) / 1024);

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `schedule-${year}-${String(month).padStart(2, "0")}-${studioKey}-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setLastInfo({ elapsedMs, sizeKb });
    } catch (e) {
      alert(`画像化に失敗: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: "1.5rem 2rem", color: "#fff" }}>
      <h1 style={{ fontSize: "1.2rem", margin: 0 }}>
        Phase 7-G スケジュール画像デモ
      </h1>
      <p style={{ fontSize: "0.8rem", opacity: 0.7, marginTop: "0.5rem" }}>
        1080×1920 のスケジュール画像テンプレートを React でレンダリング、html-to-image で PNG 出力。
        サンプルデータ（4月の代講・休講・振替）入り。
      </p>

      {/* コントロール */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          margin: "1rem 0",
          flexWrap: "wrap",
        }}
      >
        <label style={{ fontSize: "0.85rem" }}>
          校舎{" "}
          <select
            value={studioKey}
            onChange={(e) => setStudioKey(e.target.value)}
            style={selectStyle}
          >
            <option value="hamasen">浜線校</option>
            <option value="hakuzan">白山校</option>
            <option value="obiyama">帯山校</option>
            <option value="kengun">健軍校</option>
          </select>
        </label>
        <label style={{ fontSize: "0.85rem" }}>
          年{" "}
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10) || 2026)}
            style={{ ...selectStyle, width: 90 }}
          />
        </label>
        <label style={{ fontSize: "0.85rem" }}>
          月{" "}
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value, 10))}
            style={selectStyle}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {m}月
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: "0.85rem" }}>
          プレビュー倍率{" "}
          <select
            value={scale}
            onChange={(e) => setScale(parseFloat(e.target.value))}
            style={selectStyle}
          >
            <option value={0.3}>30%</option>
            <option value={0.4}>40%</option>
            <option value={0.45}>45%</option>
            <option value={0.5}>50%</option>
            <option value={0.7}>70%</option>
            <option value={1}>100% (等倍)</option>
          </select>
        </label>

        <label style={{ fontSize: "0.85rem" }}>
          データ{" "}
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            style={selectStyle}
          >
            <option value="real">実データ（API）</option>
            <option value="sample">サンプル</option>
            <option value="stress">全日4行（負荷確認）</option>
            <option value="remark-light">備考:通常密度×短文8字</option>
            <option value="remark-mid">備考:振替併記×中文19字</option>
            <option value="remark-long">備考:長文37字</option>
            <option value="remark-xlong">備考:超長文62字（折返し）</option>
            <option value="remark-stress">備考:全日4行×全行備考</option>
          </select>
        </label>


        <span style={{ fontSize: "0.8rem", opacity: 0.85 }}>
          縮小 {Math.round(fitInfo.fitScale * 100)}%
          {fitInfo.overflow && (
            <span style={{ color: "#ff8080", fontWeight: 700, marginLeft: 6 }}>
              ⚠ 収まらず下部が欠落
            </span>
          )}
        </span>

        <button onClick={handleDownload} disabled={busy} style={buttonStyle(busy)}>
          {busy ? "生成中…" : "PNG をダウンロード"}
        </button>
        {lastInfo && (
          <span style={{ fontSize: "0.8rem", opacity: 0.8 }}>
            {lastInfo.elapsedMs}ms / 約 {lastInfo.sizeKb}KB
          </span>
        )}
      </div>

      <p style={{ fontSize: "0.75rem", opacity: 0.5, margin: "0 0 0.5rem" }}>
        実出力は 1080×1920 px（プレビューは縮小表示）。全日付が 1 枚に収まっているか確認してください。
        {source === "real" && loadError && (
          <span style={{ color: "#ff8080", marginLeft: 8 }}>
            取得エラー: {loadError}
          </span>
        )}
        {source === "real" && !realData && !loadError && (
          <span style={{ marginLeft: 8 }}>読み込み中…</span>
        )}
      </p>

      {/* 縮小プレビュー */}
      <div
        style={{
          width: 1080 * scale,
          height: 1920 * scale,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.25)",
          borderRadius: 6,
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: 1080,
            height: 1920,
          }}
        >
          <div ref={targetRef}>
            <ScheduleImageTemplate
              year={year}
              month={month}
              studioId={studioId}
              entries={entries}
              onFit={setFitInfo}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const selectStyle = {
  background: "#1a1d26",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.3)",
  borderRadius: 4,
  padding: "0.25rem 0.5rem",
  fontSize: "0.85rem",
};

const buttonStyle = (busy) => ({
  background: "#2b90ff",
  color: "#fff",
  border: "none",
  borderRadius: 999,
  padding: "0.5rem 1.25rem",
  fontSize: "0.9rem",
  cursor: busy ? "wait" : "pointer",
});
